// English strings for P6-C (APPS-04/05, INTEGRATION-01…05, NOTIFY-03 SMS, STORE-03, ORG-02) (D-013: English only). Owned by that worker;
// spread into `en` as `en.integrations`, so keys read `t("integrations.…")`. Copy rules: DESIGN_SYSTEM §9.6.
export const integrationsStrings = {
  developers: {
    title: "Developers",
    lead: "API keys, the MCP server and webhooks for your own software and AI apps.",
    testMode: {
      title: "Test mode",
      on: "Test mode is on. Test keys work and test orders stay out of reports and limits.",
      off: "Test mode is off. Only live keys work.",
      turnOn: "Turn on test mode",
      turnOff: "Turn off test mode",
      ownerOnly: "Only an Owner can switch test mode.",
      switchLabel: "Test mode",
      onShort: "Test keys work. Test orders stay out of reports.",
      offShort: "Only live keys work.",
      switched: "Test mode is {state}",
      stateOn: "on",
      stateOff: "off",
    },
    quick: {
      title: "Quick start",
      base: "Base address",
      auth: "Send the key as a Bearer token. Each key may make up to 120 requests a minute.",
      example: "Example: list your orders",
      endpoints: "What the API can do",
      prefix: "Store paths start with /orgs/:org/stores/:store. Webhooks sit under /orgs/:org.",
      copy: "Copy",
      copied: "Copied",
      ep: {
        orders: "Read orders",
        products: "Read products",
        customers: "Read customers",
        inventory: "Read and adjust stock",
        webhooks: "Manage webhooks",
        writes: "Change orders, products and stock",
      },
    },
    keys: {
      title: "API keys",
      lead: "For your own apps and services. A key acts with your permissions, never more.",
      empty: "No API keys yet.",
      emptyCta: "Create your first key",
      emptyHint: "Connect an app, a sheet or your own service.",
      create: "Create API key",
      createShort: "New key",
      seePlans: "See plans",
      planNone: "Your plan doesn't include API keys",
      planFull: "You've used all {limit} API keys on your plan",
      planHint: "Pick a plan with API keys, or revoke a key you no longer use.",
      name: "Key name",
      namePlaceholder: "e.g. Accounting sync",
      mode: "Mode",
      live: "Live",
      test: "Test",
      testNeedsMode: "Turn on test mode first",
      scopes: "Access",
      scopesHelp: "Choose what this key may do. It can never do more than you can.",
      all: "Select all",
      none: "Clear",
      scope: {
        orders_read: "See orders",
        orders_update: "Change orders",
        products_read: "See products",
        products_write: "Edit products",
        customers_read: "See customers",
        inventory_read: "See stock",
        inventory_adjust: "Adjust stock",
        webhooks_manage: "Webhooks",
      },
      scopeHelp: {
        orders_read: "Orders, items, status and payments",
        orders_update: "Change status, notes and tags",
        products_read: "Products, variants and prices",
        products_write: "Create products, change prices and details",
        customers_read: "Names, phones and addresses",
        inventory_read: "Stock levels per variant",
        inventory_adjust: "Add, remove or count stock",
        webhooks_manage: "Add and remove webhooks",
      },
      read: "Read",
      write: "Change",
      preset: "Permission set",
      presets: {
        read_only: "Read only",
        orders: "Orders & delivery",
        catalog: "Catalog & stock",
        full: "Full access",
        custom: "Custom",
      },
      presetHelp: {
        read_only: "Sees orders, products, customers and stock. Changes nothing.",
        orders: "Reads and updates orders; sees customers, products and stock.",
        catalog: "Creates and edits products, adjusts stock.",
        full: "Everything the API offers, up to your own permissions.",
        custom: "Pick each permission yourself below.",
      },
      stores: "Stores",
      storesAll: "Every store",
      storesSome: "Only chosen stores",
      storesHelp:
        "A limited key sees other stores as not found, and can't adjust stock or manage webhooks.",
      storesPick: "Choose at least one store.",
      storeCount: "{count} store limit",
      rateLimit: "Requests a minute",
      ratePlan: "Plan limit (120)",
      rateOwn: "Own limit",
      rateHelp: "Lower the limit for a script you don't fully trust. {min}–{max} a minute.",
      rateInvalid: "Use a number from {min} to {max}.",
      rateBadge: "{count}/min",
      limits: "Limits",
      edit: "Edit",
      editTitle: "Edit {name}",
      save: "Save changes",
      saved: "Key updated",
      editHelp: "Changes apply on the key's next request. The key itself stays the same.",
      expiryKeep: "Keep",
      expiresOn: "Expires {date}",
      expiry: "Expires",
      expiryNever: "Never",
      expiry30: "30 days",
      expiry90: "90 days",
      expiry365: "1 year",
      ipAllow: "Allowed IP addresses (optional)",
      ipPlaceholder: "203.0.113.4, 198.51.100.0/24",
      ipHelp: "Only these addresses can use the key. Leave empty to allow any.",
      ipInvalid: "Not an IP address: {value}",
      created: "Key created",
      copyOnce: "Copy this key now. For your security it is shown only once.",
      secretTitle: "Your new API key",
      secretHelp: "Store it somewhere safe, like a password manager. You won't see it again.",
      copyKey: "Copy key",
      done: "Done",
      revoke: "Revoke",
      revokeTitle: "Revoke {name}?",
      revokeBody: "Apps using this key stop working at once. This can't be undone.",
      revoked: "Key revoked",
      rotate: "Rotate",
      rotateTitle: "Rotate {name}?",
      rotateBody:
        "You get a new key with the same access. The old key stops working at once, so update your app right after.",
      rotated: "Key rotated",
      state: { active: "Active", revoked: "Revoked", expired: "Expired" },
      lastUsed: "Last used",
      never: "Never",
      neverUsed: "Not used yet",
      expires: "Expires {date}",
      noExpiry: "No expiry",
      ipCount: "{count} IP limit",
      createdOn: "Created {date}",
      showOld: "Show revoked and expired ({count})",
      hideOld: "Hide revoked and expired",
      menu: "Actions for {name}",
    },
    webhooks: {
      title: "Webhooks",
      lead: "We send an HTTPS POST to your address when something happens in your store.",
      empty: "No webhook endpoints yet.",
      emptyCta: "Add your first endpoint",
      emptyHint: "Get told the moment an order is placed or paid.",
      add: "Add endpoint",
      addShort: "New endpoint",
      edit: "Edit endpoint",
      url: "Endpoint address (https)",
      urlPlaceholder: "https://example.com/webhooks/adyvance",
      urlInvalid: "Use a full https address.",
      description: "Description (optional)",
      descriptionPlaceholder: "e.g. Order sync to warehouse",
      events: "Events",
      eventsCount: "{count} events",
      allEvents: "All events",
      allEventsHelp: "Includes events added later.",
      pickEvents: "Choose events",
      noEvents: "Choose at least one event",
      group: {
        order: "Orders",
        payment: "Payments",
        refund: "Refunds",
        product: "Products",
        inventory: "Stock",
        customer: "Customers",
        shipment: "Shipping",
        return: "Returns",
        invoice: "Invoices",
        cod: "Cash on delivery",
        settlement: "Settlements",
        pos: "Point of sale",
        other: "Other",
      },
      created: "Endpoint added",
      saved: "Endpoint saved",
      secretOnce: "Copy this signing secret now. It is shown only once.",
      secretTitle: "Signing secret",
      secretHelp:
        "Use it to check the ady-signature header on each delivery. You won't see it again.",
      copySecret: "Copy secret",
      test: "Send test",
      testOk: "Test delivered",
      testFailed: "Test not delivered",
      testResult: "{code} · {ms} ms",
      delete: "Delete",
      deleteTitle: "Delete this endpoint?",
      deleteBody: "Deliveries to {url} stop at once. This can't be undone.",
      deleted: "Endpoint deleted",
      pause: "Pause",
      resume: "Resume",
      paused: "Endpoint paused",
      resumed: "Endpoint resumed",
      rotate: "New signing secret",
      rotateTitle: "Create a new signing secret?",
      rotateBody: "The old secret keeps working for 24 hours, so you have time to switch.",
      rotated: "New signing secret created",
      oldSecretUntil: "Old secret works until {date}",
      deliveries: "Deliveries",
      deliveriesTitle: "Recent deliveries",
      deliveriesEmpty: "Nothing sent yet. Events show here as they are delivered.",
      replay: "Send again",
      replayed: "Sent again",
      retryAt: "Retry {date}",
      attempt: "Attempt {count}",
      dstate: { pending: "Waiting", delivered: "Delivered", failed: "Failed", dead: "Gave up" },
      state: { active: "Active", disabled: "Paused", auto_disabled: "Paused after failures" },
      failures: "{count} failures in a row",
      menu: "Actions for {url}",
      signing:
        "Each delivery is signed with your secret and retried for up to 24 hours if your server doesn't answer.",
    },
    sms: {
      title: "SMS delivery",
      blocked:
        "SMS sending is not set up yet. Order messages are kept in the send log and nobody is texted until an SMS provider account is connected.",
      configured:
        "An SMS provider is configured ({provider}). Delivery reports update each message.",
    },
  },
  // INTEGRATION-07 (D-056): marketplaces on Apps → Integrations.
  // Apps → Developer (D-090): API keys and the MCP server.
  apiKeysPage: {
    title: "API keys",
    lead: "Keys for your own apps, scripts and AI tools. A key acts with your permissions, never more, and only with the access you give it.",
  },
  mcp: {
    title: "MCP server",
    lead: "Connect AI apps like Claude and ChatGPT to your store. They can look things up and, with a person's yes each time, make changes.",
    server: "Server address",
    status: "Ready",
    statusHelp: "Works with any AI app that supports remote MCP servers.",
    howTitle: "Connect an AI app",
    signInWay: "Sign in with Adyvance",
    signInWayHelp:
      "For Claude.ai, ChatGPT and other apps that sign in. You approve the app and choose what it may do.",
    keyWay: "API key",
    keyWayHelp: "For Claude Code, Cursor, VS Code and Claude Desktop. Paste the address and a key.",
    makeKey: "Make an MCP key",
    keyNeeded: "Make a key first, then copy its secret into the config below.",
    keyPlaceholder: "YOUR_API_KEY",
    steps: {
      claudeAi1: "In Claude, open Settings → Connectors → Add custom connector.",
      claudeAi2: "Paste the server address and choose Add.",
      claudeAi3: "Choose Connect, sign in to Adyvance and approve.",
      chatgpt1:
        "In ChatGPT, open Settings → Apps & Connectors → Advanced, and turn on developer mode.",
      chatgpt2: "Create a connector with the server address and OAuth sign-in.",
      chatgpt3: "Sign in to Adyvance and approve.",
      claudeCode: "Run this in a terminal:",
      cursor: "Add this to .cursor/mcp.json:",
      vscode: "Add this to .vscode/mcp.json:",
      desktop: "Add this to claude_desktop_config.json (needs Node.js):",
    },
    clients: {
      claudeAi: "Claude.ai",
      chatgpt: "ChatGPT",
      claudeCode: "Claude Code",
      cursor: "Cursor",
      vscode: "VS Code",
      desktop: "Claude Desktop",
    },
    toolsTitle: "What AI apps can do",
    toolsLead:
      "Each tool needs its permission. Tools that change something show a preview first and run only after the person agrees.",
    toolRead: "Looks up",
    toolWrite: "Changes, after a yes",
    tool: {
      list_stores: "List your stores",
      search_orders: "Find orders",
      get_order: "Open an order",
      search_products: "Find products",
      get_product: "Open a product",
      list_categories: "List categories",
      search_customers: "Find customers",
      get_customer: "Open a customer",
      check_stock: "Check stock",
      update_order_status: "Change an order's status",
      update_order_details: "Edit an order's note or tags",
      create_product: "Create a product",
      update_product: "Edit a product",
      update_variant: "Change a price or SKU",
      adjust_stock: "Adjust stock",
    },
    appsTitle: "Connected AI apps",
    appsLead: "Apps people approved with Sign in with Adyvance. Disconnecting stops them at once.",
    appsEmpty: "No AI apps connected yet.",
    appsEmptyHint: "Connect Claude.ai or ChatGPT above; it shows up here.",
    disconnect: "Disconnect",
    disconnectTitle: "Disconnect {name}?",
    disconnectBody: "The app loses access at once. Someone can connect it again later.",
    disconnected: "App disconnected",
    connectedOn: "Connected {date}",
    guide: "Setup guide on GitHub",
    safety:
      "Every request is checked against the key's permissions and the person's role at that moment.",
  },
  connect: {
    title: "Connect {app} to Adyvance",
    lead: "{app} wants to use your store through MCP.",
    loading: "Checking the request…",
    redirect: "After you choose, you go back to {host}.",
    shop: "Shop",
    stores: "Stores it may use",
    access: "What it may do",
    asked: "The app asked for: {scopes}",
    allow: "Allow",
    deny: "Don't allow",
    note: "The app never gets more than you can do. Disconnect it any time in Apps → Developer → MCP server.",
    noShop: "You need a shop where you manage API keys to connect an AI app.",
    invalid: "This request can't be used",
    invalidHint: "Go back to the AI app and try connecting again.",
    back: "Back to the AI app",
    done: "Done. Going back to the AI app…",
    planLocked: "Connect AI tools (MCP) is part of the Business plan.",
  },
  marketplaces: {
    title: "Marketplaces",
    lead: "Sell on marketplaces and manage them from Adyvance: orders, stock and payouts in one place.",
    daraz: "Your Daraz store: orders in one list, one stock, labels and payouts.",
    connect: "Connect",
    manage: "Manage",
  },
  apps: {
    title: "Integrations",
    lead: "Add tracking pixels, chat buttons and verification tags. Only the ones you turn on load on your store.",
    kinds: {
      ga4: "Google Analytics 4",
      gtm: "Google Tag Manager",
      meta_pixel: "Meta Pixel",
      tiktok_pixel: "TikTok Pixel",
      clarity: "Microsoft Clarity",
      verification_meta: "Site verification tags",
      whatsapp: "WhatsApp chat button",
      messenger: "Messenger chat button",
      tawk: "Tawk.to chat",
    },
    about: {
      ga4: "See visitors, traffic sources and what they do on your store.",
      gtm: "Manage your tracking tags in one place without changing the store.",
      meta_pixel: "Measure and improve your Facebook and Instagram ads.",
      tiktok_pixel: "Measure the sales your TikTok ads bring in.",
      clarity: "Watch heatmaps and session recordings to see where shoppers get stuck.",
      whatsapp: "Show a WhatsApp chat button so shoppers can message you.",
      messenger: "Show a Messenger chat button linked to your Facebook page.",
      tawk: "Add Tawk.to live chat so shoppers can talk to you from any page.",
    },
    fields: {
      measurement_id: "Measurement ID",
      container_id: "Container ID",
      pixel_id: "Pixel ID",
      project_id: "Project ID",
      phone: "WhatsApp number",
      page: "Page username",
      property_id: "Property ID",
      widget_id: "Widget ID",
    },
    category: {
      ga4: "Analytics · Google",
      gtm: "Tags · Google",
      meta_pixel: "Ads · Meta",
      tiktok_pixel: "Ads · TikTok",
      clarity: "Insights · Microsoft",
      whatsapp: "Chat · WhatsApp",
      messenger: "Chat · Meta",
      tawk: "Live chat · Tawk.to",
    },
    steps: {
      ga4: {
        a: "In Google Analytics, open Admin → Data streams.",
        b: "Pick your website stream.",
        c: "Copy the Measurement ID (starts with G-).",
      },
      gtm: {
        a: "Open Google Tag Manager.",
        b: "Pick your container.",
        c: "Copy the ID at the top (starts with GTM-).",
      },
      meta_pixel: {
        a: "Open Meta Events Manager.",
        b: "Pick your pixel (dataset).",
        c: "Copy the number under its name.",
      },
      tiktok_pixel: {
        a: "In TikTok Ads Manager, open Tools → Events.",
        b: "Pick your web pixel.",
        c: "Copy the Pixel ID.",
      },
      clarity: {
        a: "Open your Clarity project.",
        b: "Go to Settings → Overview.",
        c: "Copy the Project ID.",
      },
      whatsapp: {
        a: "Use the number shoppers should message.",
        b: "It must have WhatsApp.",
        c: "Start with +977.",
      },
      messenger: {
        a: "Open your Facebook page.",
        b: "Look at its link: facebook.com/username.",
        c: "Copy the username.",
      },
      tawk: {
        a: "In Tawk.to, open Administration → Chat Widget.",
        b: "Copy the Direct Chat Link.",
        c: "Paste it here. Both IDs fill in.",
      },
    },
    setup: {
      connect: "Connect",
      where: "Where to find it",
      open: "Open {name}",
      pasteHint: "You can paste the ID or the whole code snippet.",
      invalid: "This doesn't look like a {field}. Example: {example}",
      live: "Live on your store",
      liveHelp: "Turn off to pause it. Your IDs are kept.",
      metaEvents:
        "Sends page views, product views, add to cart, checkout started and purchases. Meta takes no Nepali rupees, so values go in Indian rupees (Rs 1.60 = ₹1). Meta's automatic events stay off.",
      tiktokEvents:
        "Sends page views, product views, add to cart, checkout started and purchases. TikTok takes no Nepali rupees, so values go in Indian rupees (Rs 1.60 = ₹1).",
      shopEvents:
        "Sends page views, product views, add to cart, checkout started and purchases with their value in rupees.",
      gtmEvents:
        "Your tags get product views, add to cart, checkout started and purchases as Google Analytics ecommerce events. Tags with custom code don't run: the store's security blocks them.",
      notAdded: "Not added",
      live_badge: "Live",
      paused: "Paused",
      fillAll: "Fill in every field",
      close: "Close",
      cancel: "Cancel",
      add: "Add to store",
    },
    searchLabel: "Search apps",
    searchHint: "Search apps…",
    installedTitle: "Installed ({count})",
    availableTitle: "Available ({count})",
    installedBadge: "Installed",
    settings: "Settings",
    add: "Add {name}",
    settingsTitle: "{name} settings",
    noMatch: "No apps match your search.",
    idLabel: "ID",
    idHelp: "Paste the ID from the provider, for example G-ABC123.",
    enabled: "On",
    disabled: "Off",
    save: "Save",
    remove: "Remove",
    saved: "Integration saved",
    removed: "Integration removed",
    feeds: {
      title: "Product feeds",
      lead: "Share your catalogue with Google Merchant Center and Meta. Only products visible on your store are included.",
      google: "Google feed",
      meta: "Meta feed",
      on: "Feed on",
      off: "Feed off",
      saved: "Feed updated",
    },
  },
  kyc: {
    title: "Business verification",
    lead: "Verify your business to unlock payouts and higher limits. Only Owners can see these details.",
    state: {
      draft: "Not submitted",
      submitted: "Under review",
      needs_info: "More information needed",
      approved: "Verified",
      rejected: "Not approved",
    },
    entityType: "Business type",
    entity: {
      individual: "Individual",
      sole_proprietorship: "Sole proprietorship",
      partnership: "Partnership",
      private_limited: "Private limited company",
      public_limited: "Public limited company",
      other: "Other",
    },
    registeredName: "Registered name",
    panVat: "PAN / VAT number",
    registrationNo: "Registration number",
    signatory: "Authorised signatory",
    bankName: "Bank",
    bankAccount: "Bank account number",
    bankAccountHelp: "Stored encrypted. Only the last 4 digits are shown later.",
    bankSaved: "Account ending {last4}",
    documents: "Documents",
    noDocuments:
      "No documents yet. Upload them as verification documents in the media library first.",
    save: "Save",
    saved: "Details saved",
    submit: "Submit for review",
    submitted: "Submitted for review",
    missing: "Still needed: {fields}",
    reviewNote: "Reviewer note",
    review: {
      title: "Business verification review",
      lead: "Submitted business verifications, oldest first.",
      empty: "Nothing waiting for review.",
      approve: "Approve",
      needsInfo: "Ask for more information",
      reject: "Reject",
      note: "Note to the vendor",
      decided: "Decision recorded",
      documentsHint:
        "Open each document and check it matches the details before you approve. Approving gives the shop the blue tick.",
      // D-068: the full review card, the state tabs and the agreement PDF.
      tabs: {
        submitted: "Waiting",
        needs_info: "Asked for more",
        approved: "Verified",
        rejected: "Not approved",
      },
      emptyState: "No shops here.",
      open: "Review",
      close: "Close",
      sentOn: "Sent {date}",
      decidedOn: "Decided {date}",
      owner: "Owner: {name}",
      details: "Business details",
      bank: "Bank account (business)",
      documents: "Registration documents",
      agreement: "Signed agreement",
      agreementMissing: "Not submitted (optional)",
      agreementOn: "Uploaded {date}",
      openFile: "Open",
      sampleFile: "Sample: no file",
      accountEnding: "•••• {last4}",
      noteRequired: "Write a note to ask for more information or to reject.",
      fields: {
        registered_name: "Registered business name",
        entity_type: "Business type",
        pan_vat: "PAN number",
        registration_no: "Registration number",
        registered_address: "Registered address",
        business_phone: "Business phone",
        bank_name: "Bank name",
        bank_account: "Account number",
        bank_account_name: "Account name",
        bank_branch: "Branch",
      },
      docKinds: {
        registration_certificate: "Registration certificate",
        pan_vat_certificate: "PAN / VAT certificate",
        other: "Other document",
      },
      // D-068: the Unverified badge, force verify and remove verification.
      unverified: "Unverified",
      unverifiedOpen: "Open this shop's verification",
      verifiedFilter: "Verified",
      unverifiedFilter: "Unverified",
      forced: "Force verified",
      forcedNote: "Force verified: {reason}",
      force: {
        verify: "Force verify",
        verifyTitle: "Force verify {name}?",
        verifyBody:
          "The shop gets the blue tick now, even if details or documents are missing. Your reason stays with Adyvance staff and the activity log; the shop doesn't see it.",
        verifyReason: "Why are you verifying this shop?",
        verifyReasonPh: "For example: documents checked in person at the office",
        verified: "Shop verified",
        revoke: "Remove verification",
        revokeTitle: "Remove verification from {name}?",
        revokeBody:
          "The blue tick goes. The shop sees your reason and can send its details again with Request change.",
        revokeReason: "Reason (the shop sees this)",
        revokeReasonPh: "For example: the PAN certificate has expired",
        revoked: "Verification removed",
        reasonShort: "Write at least 3 characters.",
        cancel: "Cancel",
      },
      template: {
        title: "Vendor agreement",
        lead: "The PDF shops download, sign and upload with their store verification.",
        none: "No agreement uploaded yet. Shops can't download one until you add it.",
        current: "{name} · uploaded {date}",
        view: "View",
        upload: "Upload PDF",
        replace: "Replace PDF",
        uploading: "Uploading…",
        saved: "Agreement uploaded. Shops download this version from now on.",
        wrongType: "Upload a PDF of 10 MB or less.",
      },
    },
  },
  errors: {
    vault:
      "Secure storage for secrets is not connected yet, so this cannot be saved. Nothing was changed.",
    urlBlocked: "This address cannot receive calls from Adyvance. Use a public https address.",
  },
} as const;
